import React, { useEffect, useContext, useState, useRef } from 'react';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import PhotoBookLayout from './PhotoBookLayout';
import { layouts } from './layouts';
import { functions, httpsCallable } from '../../firebase'; // Import Firebase functions
import { ProgressContext } from '../../App';
import useLazyBackgroundImages from '../../hooks/useLazyBackgroundImages'; // Import the lazy loading hook 
import { Button } from '@mui/material'; // Import Button for edit action

// Define chunk size constant
const MAX_PAGES_PER_CHUNK = 10;
// For booklet, each "page" in PDF represents a double-sided sheet, so max sheets is half the max pages.
const MAX_SHEETS_PER_CHUNK = Math.floor(MAX_PAGES_PER_CHUNK / 2);

// Font scaling is now handled directly by PhotoBookLayout

class PhotoBook {
  constructor() {
    this.pages = [];
  }

  addPage(
    layoutId, photos, 
    leftPageText, rightPageText,
    leftPageDisplayDate, leftPageDisplayCity,
    rightPageDisplayDate, rightPageDisplayCity,
    photo_place_names, explanation, 
    adjustedLeftPageFontSize, adjustedRightPageFontSize,
    leftPageData, rightPageData,
    ai_batch_id, source_entry_ids, // NEW: batch and source entry IDs
    placeholderModifications        // NEW: placeholder modifications
  ) {
    this.pages.push({ 
      layoutId, 
      photos, 
      leftPageText, 
      rightPageText,
      leftPageDisplayDate, 
      leftPageDisplayCity,
      rightPageDisplayDate, 
      rightPageDisplayCity,
      photo_place_names, 
      explanation, 
      adjustedLeftPageFontSize, 
      adjustedRightPageFontSize,
      leftPageData, 
      rightPageData,
      ai_batch_id, 
      source_entry_ids,
      placeholderModifications     // NEW: placeholder modifications
    });
  }

  render(spreadPreviewWidthUsedForFitting, onAlbumLoadedCallback, onRequestEditBatch, onLayoutPlanUpdate, onPhotoCropChangeInPlan, albumId) {
    return <PhotoBookRenderer 
      pages={this.pages} 
      spreadPreviewWidthUsedForFitting={spreadPreviewWidthUsedForFitting}
      onAlbumLoaded={onAlbumLoadedCallback}
      onRequestEditBatch={onRequestEditBatch} // NEW
      onLayoutPlanUpdate={onLayoutPlanUpdate} // Pass onLayoutPlanUpdate to PhotoBookRenderer
      onPhotoCropChangeInPlan={onPhotoCropChangeInPlan} // PASS THIS
      albumId={albumId}
    />;
  }
}

const PhotoBookRenderer = ({ pages, spreadPreviewWidthUsedForFitting, onAlbumLoaded, onRequestEditBatch, onLayoutPlanUpdate, onPhotoCropChangeInPlan, albumId }) => { // ADD onLayoutPlanUpdate to props
  // Initialize the lazy loading hook
  useLazyBackgroundImages();
  
  const [pdfType, setPdfType] = useState('normal');
  const layoutRefs = useRef([]);
  const [combinedLayoutPlan, setCombinedLayoutPlan] = useState(null);
  const [editingSpreadIndex, setEditingSpreadIndex] = useState(null); // null or index of the spread being edited
  
  // Consume specific progress setters from context
  const {
    setPdfProgress,
    setShowPdfProgress,
    setPdfProgressLabel,
    setLoading
  } = useContext(ProgressContext);
  const [isPrintLayout, setIsPrintLayout] = useState(false);
  
  // Initialize or update the combinedLayoutPlan when pages change
  useEffect(() => {
    if (pages && pages.length > 0) {
      // Create a combined layout plan from the pages prop for saving purposes.
      // The `pages` prop itself is used for rendering PhotoBookLayout components.
      const planForSaving = pages.map((pageFromBook, index) => {
        // `pageFromBook` is an object from `this.pages` array in PhotoBook class
        // Get placeholder modifications for this specific layout page
        const placeholderModificationsArray = pageFromBook.placeholderModifications || [];

        return {
          layout_id: pageFromBook.layoutId,
          photos: pageFromBook.photos.map(photo => {
            if (typeof photo === 'string') {
              return { url: photo }; // cropInfo would be default or lost
            }
            return { // Simply return the photo object as is, or select specific fields for saving
              ...photo, // This includes photo.id, photo.url, photo.cropInfo, etc.
                       // No need to merge with local photoMetadata anymore.
            };
          }),
          // Per-page data
          left_page_text: pageFromBook.leftPageText,
          right_page_text: pageFromBook.rightPageText,
          left_page_display_date: pageFromBook.leftPageDisplayDate,
          left_page_display_city: pageFromBook.leftPageDisplayCity,
          right_page_display_date: pageFromBook.rightPageDisplayDate,
          right_page_display_city: pageFromBook.rightPageDisplayCity,
          adjusted_left_page_font_size: pageFromBook.adjustedLeftPageFontSize,
          adjusted_right_page_font_size: pageFromBook.adjustedRightPageFontSize,
          // Additional data
          photo_place_names: pageFromBook.photo_place_names,
          explanation: pageFromBook.explanation,
          leftPageData: pageFromBook.leftPageData,
          rightPageData: pageFromBook.rightPageData,
          ai_batch_id: pageFromBook.ai_batch_id,
          source_entry_ids: pageFromBook.source_entry_ids,
          placeholderModifications: placeholderModificationsArray, // Use directly from pageFromBook
        };
      });
      setCombinedLayoutPlan(planForSaving);
    }
  }, [pages]); // UPDATED dependency array

  const getRenderablePhotoData = httpsCallable(functions, 'getRenderablePhotoData');

  const googlePhotosBaseUrl = (url) => (
    url ? url.replace(/=(?:d|w\d+(?:-[a-z0-9]+)*)$/i, '') : url
  );

  const driveFileIdFromUrl = (url) => {
    if (!url) return null;
    try {
      const parsed = new URL(url);
      const idParam = parsed.searchParams.get('id');
      if (idParam) return idParam;

      if (parsed.hostname === 'lh3.googleusercontent.com') {
        const match = parsed.pathname.match(/^\/d\/([^/]+)/);
        return match ? decodeURIComponent(match[1]) : null;
      }

      return null;
    } catch (error) {
      return null;
    }
  };

  const entryIdFromPageContext = (pageContext) => {
    if (pageContext?.entryId || pageContext?.journalEntryId) {
      return pageContext.entryId || pageContext.journalEntryId;
    }
    const sourceEntryIds = pageContext?.source_entry_ids || pageContext?.sourceEntryIds;
    return Array.isArray(sourceEntryIds) && sourceEntryIds.length === 1
      ? sourceEntryIds[0]
      : undefined;
  };

  const fetchRenderablePhotoData = async (photoObject, src, pageContext) => {
    if (!photoObject && !src) return null;

    const driveFileId = photoObject?.driveFileId ||
      (photoObject?.isDriveFile ? photoObject.mediaId : null) ||
      driveFileIdFromUrl(src);

    const isDriveFile = photoObject && (
      photoObject.isDriveFile ||
      photoObject.driveFileId ||
      (photoObject.url && photoObject.url.includes('drive.google.com')) ||
      (photoObject.url && photoObject.url.includes('drive.usercontent.google.com')) ||
      (photoObject.baseUrl && photoObject.baseUrl.includes('drive.google.com')) ||
      (photoObject.baseUrl && photoObject.baseUrl.includes('drive.usercontent.google.com'))
    );

    const payload = isDriveFile || driveFileId ? {
      driveFileId,
      mimeType: photoObject?.mimeType,
      filename: photoObject?.filename,
      albumId,
      entryId: photoObject?.journalEntryId || photoObject?.entryId || entryIdFromPageContext(pageContext),
    } : {
      googlePhotoBaseUrl: googlePhotosBaseUrl(src),
      source: 'picker',
      mimeType: photoObject?.mimeType,
      filename: photoObject?.filename,
      accessToken: localStorage.getItem('googleAccessToken') || undefined,
    };

    try {
      const result = await getRenderablePhotoData(payload);
      return result.data.image;
    } catch (error) {
      console.error('Error fetching renderable photo data:', error);
      return null;
    }
  };

  // Function to extract background URLs from the layout object
  const extractLayoutBackgroundUrls = (layout) => {
    const backgroundUrls = [];
    
    // Extract the background from leftPage and rightPage if they exist
    if (layout.background) {
      backgroundUrls.push(layout.background); // Add the layout background to the list
    }
    
    return backgroundUrls;
  };

  
  // Handle photo crop/zoom changes
  const handlePhotoCropChange = (layoutIndex, photoIndexInLayout, newCropInfo) => {
    // Call the new prop passed from App.js
    // The photoId is not strictly needed by App.js if we use photoIndexInLayout
    if (onPhotoCropChangeInPlan) {
      onPhotoCropChangeInPlan(layoutIndex, photoIndexInLayout, newCropInfo);
    } else {
      console.warn("PhotoBookRenderer: onPhotoCropChangeInPlan prop is not defined.");
    }
  };

  // Save album to JSON file
  const saveAlbum = () => {
    if (!combinedLayoutPlan) return;
    
    const albumData = {
      albumVersion: "2.1",
      createdAt: new Date().toISOString(),
      layouts: combinedLayoutPlan.map(layout => ({
        layout_id: layout.layout_id,
        // Per-page fields
        left_page_text: layout.left_page_text,
        right_page_text: layout.right_page_text,
        left_page_display_date: layout.left_page_display_date,
        left_page_display_city: layout.left_page_display_city,
        right_page_display_date: layout.right_page_display_date,
        right_page_display_city: layout.right_page_display_city,
        adjusted_left_page_font_size: layout.adjusted_left_page_font_size,
        adjusted_right_page_font_size: layout.adjusted_right_page_font_size,
        photo_place_names: layout.photo_place_names,
        explanation: layout.explanation,
        leftPageData: layout.layout_id === 'cover' ? layout.leftPageData : undefined,
        rightPageData: layout.layout_id === 'cover' ? layout.rightPageData : undefined,
        placeholderModifications: layout.placeholderModifications || [], // NEW: Save placeholder modifications
        photos: layout.photos.map(photo => ({
          mediaId: photo.mediaId || photo.id,
          isDriveFile: photo.isDriveFile || false,
          filename: photo.isDriveFile ? photo.filename : undefined,
          mimeType: photo.isDriveFile ? photo.mimeType : undefined,
          journalEntryId: photo.journalEntryId,
          cropInfo: photo.cropInfo || { zoom: 1, crop: { x: 0, y: 0 }, croppedAreaPercentages: null }
        })),
        ai_batch_id: layout.ai_batch_id,
        source_entry_ids: layout.source_entry_ids,
      }))
    };
    
    // Create a download link for the JSON file
    const jsonStr = JSON.stringify(albumData, null, 2);
    const blob = new Blob([jsonStr], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `photo_album_${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Load album from JSON file
  const loadAlbumFromFile = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const loadedData = JSON.parse(e.target.result);
        // Verify album version
        if (!loadedData.albumVersion) {
          alert('Invalid album file format');
          return;
        }
        
        // Call the callback to handle loading the album data
        if (onAlbumLoaded && typeof onAlbumLoaded === 'function') {
          onAlbumLoaded(loadedData);
        } else {
          console.warn('No onAlbumLoaded callback provided');
          alert('Album loaded successfully, but no handler is available to process it.');
        }
      } catch (error) {
        console.error('Error parsing album file:', error);
        alert('Error loading album: ' + error.message);
      }
    };
    reader.readAsText(file);
  };
  
  // Replace user photos with authenticated data URLs before html2canvas reads pixels.
  const replaceBackgroundImagesWithBlobs = async (layoutElement, photoObjectsInLayout, layoutDefinition, pageContext) => {
    const layoutBackgroundUrls = extractLayoutBackgroundUrls(layoutDefinition);
    const placeholders = Array.from(
      layoutElement.querySelectorAll('[data-lazy-bg]')
    ).filter(el => el.dataset.src && !layoutBackgroundUrls.includes(el.dataset.src));
    
    const fetchPromises = placeholders.map(el => {
      const src = el.dataset.src;
      
      const matchingPhotoObject = photoObjectsInLayout.find(p =>
        typeof p === 'object' && (p.url === src || p.baseUrl === src)
      );
      return fetchRenderablePhotoData(matchingPhotoObject, src, pageContext)
        .then(dataUrl => ({ el, src, dataUrl }))
        .catch(error => {
          console.error('Error converting photo to data URL for', src, error);
          return { el, src, dataUrl: null };
        });
    });
    
    const results = await Promise.all(fetchPromises);
    results.forEach(({ el, src, dataUrl }) => {
      if (dataUrl) { // dataUrl is now always expected to be a base64 data URL string
        el.style.backgroundImage = `url(${dataUrl})`;
      } else {
        console.warn(`Failed to get data URL for ${src}, using direct URL as fallback for html2canvas.`);
        el.style.backgroundImage = `url(${src})`; 
      }
    });
  };

  const generatePDF = async () => {
    setIsPrintLayout(true); // Ensure print layout styles are applied before calculations

    // Show PDF progress bar
    setShowPdfProgress(true);
    setPdfProgressLabel(`Generating ${pdfType} PDF...`);
    setPdfProgress(0); // Start at 0
    setLoading(true); // Optional: Show general loading overlay too

    try {
      // Need to wait for setIsPrintLayout to apply styles and refs to be set
      await new Promise(resolve => setTimeout(resolve, 100));

      if (pdfType === 'normal') {
        await generateNormalPDF();
      } else if (pdfType === 'manual-booklet') {
        await generateManualBookletPDFs();
      } else {
        await generateBookletPDF();
      }
    } catch (error) {
        console.error("Error generating PDF:", error);
        // Optionally show an error message to the user via Snackbar/Alert
    } finally {
        // Hide PDF progress bar and loading state on completion or error
        setShowPdfProgress(false);
        setIsPrintLayout(false);
        setLoading(false);
    }
  };

  const generateNormalPDF = async () => {
    let currentPdf = null;
    let pagesInCurrentPdf = 0;
    let chunkIndex = 1;

    const pdfWidth = 297; // A4 landscape width in mm
    const pdfHeight = 210; // A4 landscape height in mm

    // Ensure layoutRefs are populated and dimensions are available
    const screenWidth = layoutRefs.current[0]?.offsetWidth;
    if (!screenWidth) {
        console.error("Cannot determine layout screen width.");
        // Handle error appropriately, maybe show a message
        return;
    }

    for (let i = 0; i < pages.length; i++) {
      // Update progress based on overall page processing
      setPdfProgress(Math.round(((i + 1) / pages.length) * 100));
      setPdfProgressLabel(`Generating ${pdfType} PDF... (Page ${i + 1}/${pages.length}) Chunk ${chunkIndex}`);

      // Check if we need a new PDF chunk
      if (currentPdf === null || pagesInCurrentPdf >= MAX_PAGES_PER_CHUNK) {
        if (currentPdf !== null) {
          // Save the completed chunk
          currentPdf.save(`photobook_normal_part_${chunkIndex}.pdf`);
          chunkIndex++;
        }
        // Start a new chunk
        currentPdf = new jsPDF('l', 'mm', 'a4');
        pagesInCurrentPdf = 0;
      }

      const layout = layoutRefs.current[i];
      if (layout) {
        await replaceBackgroundImagesWithBlobs(layout, pages[i].photos, getLayoutById(pages[i].layoutId), pages[i]);
        const canvas = await html2canvas(layout, {
          width: 3508, // Target width for high DPI
          height: 2480, // Target height for high DPI
          scale: 1, // Use target width/height instead of scale for consistency
          logging: false,
          useCORS: true, // Important for external images/blobs
        });
        layout.querySelectorAll('[data-lazy-bg]').forEach(el => el.style.backgroundImage = 'none'); // Discard loaded images from DOM to free memory
        const imgData = canvas.toDataURL('image/png');

        // Add a new page to the current PDF if it's not the first page of this chunk
        if (pagesInCurrentPdf > 0) {
          currentPdf.addPage();
        }

        currentPdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
        pagesInCurrentPdf++;

        // Yield control briefly to allow UI updates (e.g., progress bar)
        await new Promise(resolve => setTimeout(resolve, 0));

      } else {
          console.warn(`Layout ref not found for page index ${i}`);
      }
    }

    // Save the last chunk if it contains pages
    if (currentPdf !== null && pagesInCurrentPdf > 0) {
      currentPdf.save(`photobook_normal_part_${chunkIndex}.pdf`);
    }
  };

  const rotateImage = (imageData, angle) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        if (angle === 180) {
          canvas.width = img.width;
          canvas.height = img.height;
          ctx.translate(img.width, img.height);
          ctx.rotate(angle * Math.PI / 180);
        } else { // Assuming 90 or -90 if needed, but only 180 is used here
          canvas.width = img.height;
          canvas.height = img.width;
          ctx.translate(img.height / 2, img.width / 2);
          ctx.rotate(angle * Math.PI / 180);
          ctx.translate(-img.width / 2, -img.height / 2);
        }

        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL());
      };
      img.onerror = () => {
        console.error("Failed to load image for rotation");
        resolve(null); // Resolve with null on error
      }
      img.src = imageData;
    });
  };

  const generateBookletPDF = async () => {
    const pdfWidth = 297; // A4 landscape width in mm
    const pdfHeight = 210; // A4 landscape height in mm

    const pdfPages = []; // Stores image data URLs of individual half-pages
    const totalLayoutPages = pages.length;
    const screenWidth = layoutRefs.current[0]?.offsetWidth;

    if (!screenWidth) {
      console.error("Cannot determine layout screen width for booklet.");
      return;
    }

    // --- Generate and split individual pages (Progress Step 1: Canvas Generation) ---
    const generationProgressMultiplier = 0.5; // 50% of progress for this step
    setPdfProgressLabel(`Generating ${pdfType} PDF... (Canvas Generation)`);
    for (let i = 0; i < totalLayoutPages; i++) {
       // Update progress for canvas generation part
      const currentCanvasProgress = Math.round(((i + 1) / totalLayoutPages) * 100 * generationProgressMultiplier);
      setPdfProgress(currentCanvasProgress);
      setPdfProgressLabel(`Generating ${pdfType} PDF... (Processing Layout ${i + 1}/${totalLayoutPages})`);

      const layout = layoutRefs.current[i];
      if (layout) {
        await replaceBackgroundImagesWithBlobs(layout, pages[i].photos, getLayoutById(pages[i].layoutId), pages[i]);
        const canvas = await html2canvas(layout, {
          width: 3508,
          height: 2480,
          scale: 1,
          logging: false,
          useCORS: true,
        });
        layout.querySelectorAll('[data-lazy-bg]').forEach(el => el.style.backgroundImage = 'none'); // Discard loaded images from DOM to free memory

        const halfWidth = canvas.width / 2;

        const leftCanvas = document.createElement('canvas');
        leftCanvas.width = halfWidth;
        leftCanvas.height = canvas.height;
        leftCanvas.getContext('2d').drawImage(canvas, 0, 0, halfWidth, canvas.height, 0, 0, halfWidth, canvas.height);
        pdfPages.push(leftCanvas.toDataURL('image/png'));

        const rightCanvas = document.createElement('canvas');
        rightCanvas.width = halfWidth;
        rightCanvas.height = canvas.height;
        rightCanvas.getContext('2d').drawImage(canvas, halfWidth, 0, halfWidth, canvas.height, 0, 0, halfWidth, canvas.height);
        pdfPages.push(rightCanvas.toDataURL('image/png'));

        // Yield control briefly
        await new Promise(resolve => setTimeout(resolve, 0));
      } else {
          console.warn(`Layout ref not found for page index ${i}`);
      }
    }
    // --- End Canvas Generation ---


    // --- Add blank pages and rearrange for booklet ---
    const blankPage = null; // Represent blank pages as null

    // Check if the first page is already a generated cover
    const hasActualCover = pages.length > 0 && 
      (pages[0].layoutId === 'cover' || 
       (typeof pages[0].layoutId === 'string' && pages[0].layoutId.toLowerCase() === 'cover'));


    if (!hasActualCover) {
      // Add blanks for a generic separate cover if no specific cover page exists
      pdfPages.unshift(blankPage);
      pdfPages.push(blankPage);
    }

    // Pad with blanks until total logical pages is a multiple of 4 for imposition
    while (pdfPages.length % 4 !== 0) {
      pdfPages.push(blankPage);
    }
    
    if (hasActualCover) {
      const backCoverHalf = pdfPages.shift();   // index 0  → back cover
      pdfPages.push(backCoverHalf);             // …now sits at the very end
    }
    // --- 2. imposition algorithm that works for any length
    const bookletPages = [];
    for (let left = 0, right = pdfPages.length - 1;
        left < right;
        left += 2, right -= 2) {

      // front of sheet
      bookletPages.push(pdfPages[right]);     // e.g. for sheet 1: last logical page → physical left
      bookletPages.push(pdfPages[left]);      // e.g. for sheet 1: first logical page → physical right

      // back of sheet
      bookletPages.push(pdfPages[left + 1]);  // e.g. for sheet 1: second logical page
      bookletPages.push(pdfPages[right - 1]); // e.g. for sheet 1: second-to-last logical page
    }
    // --- End Page Reordering ---


    // --- Generate PDF chunks with rearranged pages (Progress Step 2: PDF Assembly) ---
    const assemblyProgressMultiplier = 0.5; // 50% of progress for this step
    const startProgress = 100 * generationProgressMultiplier; // Start from 50%
    const totalAssemblySheets = bookletPages.length / 4; // Each loop iteration processes one sheet (4 images)

    let currentPdf = null;
    let sheetsInCurrentPdf = 0;
    let chunkIndex = 1;

    for (let i = 0; i < bookletPages.length; i += 4) {
      // Update progress for PDF assembly part
      const currentAssemblySheetIndex = i / 4;
      const currentAssemblyProgress = Math.round(((currentAssemblySheetIndex + 1) / totalAssemblySheets) * 100 * assemblyProgressMultiplier);
      setPdfProgress(startProgress + currentAssemblyProgress);
      setPdfProgressLabel(`Generating ${pdfType} PDF... (Assembling Sheet ${currentAssemblySheetIndex + 1}/${totalAssemblySheets}) Chunk ${chunkIndex}`);

      if (currentPdf === null || sheetsInCurrentPdf >= MAX_SHEETS_PER_CHUNK) {
          if (currentPdf !== null) {
              currentPdf.save(`photobook_booklet_part_${chunkIndex}.pdf`);
              chunkIndex++;
          }
          currentPdf = new jsPDF('l', 'mm', 'a4');
          sheetsInCurrentPdf = 0;
      }

      // Add a new page to the current PDF if it's not the first sheet of this chunk
      if (sheetsInCurrentPdf > 0) {
          currentPdf.addPage(); // Add page for the front side of the new sheet
      }

      const rotateBack = true;

      // --- Add Front Side of the Sheet ---
      // Right side of the front page (bookletPages[i]) - physically on the left when printed
      if (bookletPages[i]) {
        // Front: never rotate
        currentPdf.addImage(bookletPages[i],       'PNG', 0,           0, pdfWidth/2, pdfHeight);
      }
      // Left side of the front page (bookletPages[i + 1]) - physically on the right when printed
      if (bookletPages[i + 1]) {
        currentPdf.addImage(bookletPages[i + 1],   'PNG', pdfWidth/2,  0, pdfWidth/2, pdfHeight);
      }

      // --- Add Back Side of the Sheet ---
      // Check if there are back pages for this sheet
      if (i + 2 < bookletPages.length) {
          currentPdf.addPage(); // Add page for the back side

          // Right side of the back page (bookletPages[i + 2]) - physically on the left when printed
          if (bookletPages[i + 2]) {
            const leftBack  = rotateBack ? await rotateImage(bookletPages[i + 2], 180) : bookletPages[i + 2];
            currentPdf.addImage(leftBack,  'PNG', 0,           0, pdfWidth/2, pdfHeight);
          }
          // Left side of the back page (bookletPages[i + 3]) - physically on the right when printed
          if (bookletPages[i + 3]) {
            const rightBack = rotateBack ? await rotateImage(bookletPages[i + 3], 180) : bookletPages[i + 3];
            currentPdf.addImage(rightBack, 'PNG', pdfWidth/2,  0, pdfWidth/2, pdfHeight);
          }
      }

      sheetsInCurrentPdf++;

      // Yield control briefly
      await new Promise(resolve => setTimeout(resolve, 0));
    }
    // --- End PDF Assembly ---

    // Save the last chunk
    if (currentPdf !== null && sheetsInCurrentPdf > 0) {
      currentPdf.save(`photobook_booklet_part_${chunkIndex}.pdf`);
    }
  };

  const generateManualBookletPDFs = async () => {
    const pdfWidth = 297; // A4 landscape width in mm
    const pdfHeight = 210; // A4 landscape height in mm

    const pdfPagesData = []; // Stores image data URLs of individual logical half-pages
    const totalLayoutPages = pages.length; // Number of spreads
    const screenWidth = layoutRefs.current[0]?.offsetWidth;

    if (!screenWidth) {
        console.error("Cannot determine layout screen width for manual booklet.");
        setShowPdfProgress(false);
        setLoading(false);
        return;
    }

    // --- Part 1: Generate Canvases for all logical half-pages ---
    const generationProgressMultiplier = 0.5; 
    setPdfProgressLabel(`Generating ${pdfType} PDF... (Canvas Generation)`);
    for (let i = 0; i < totalLayoutPages; i++) {
        const currentCanvasProgress = Math.round(((i + 1) / totalLayoutPages) * 100 * generationProgressMultiplier);
        setPdfProgress(currentCanvasProgress);
        setPdfProgressLabel(`Generating ${pdfType} PDF... (Processing Layout ${i + 1}/${totalLayoutPages})`);

        const layoutElement = layoutRefs.current[i];
        if (layoutElement) {
            await replaceBackgroundImagesWithBlobs(layoutElement, pages[i].photos, getLayoutById(pages[i].layoutId), pages[i]);
            const canvas = await html2canvas(layoutElement, {
                width: 3508, height: 2480, scale: 1, logging: false, useCORS: true,
            });
            layoutElement.querySelectorAll('[data-lazy-bg]').forEach(el => el.style.backgroundImage = 'none'); // Discard loaded images from DOM to free memory

            const halfWidth = canvas.width / 2;
            const leftCanvas = document.createElement('canvas');
            leftCanvas.width = halfWidth; leftCanvas.height = canvas.height;
            leftCanvas.getContext('2d').drawImage(canvas, 0, 0, halfWidth, canvas.height, 0, 0, halfWidth, canvas.height);
            pdfPagesData.push(leftCanvas.toDataURL('image/png'));

            const rightCanvas = document.createElement('canvas');
            rightCanvas.width = halfWidth; rightCanvas.height = canvas.height;
            rightCanvas.getContext('2d').drawImage(canvas, halfWidth, 0, halfWidth, canvas.height, 0, 0, halfWidth, canvas.height);
            pdfPagesData.push(rightCanvas.toDataURL('image/png'));

            await new Promise(resolve => setTimeout(resolve, 0));
        }
    }

    // --- Part 2: Pad and Prepare Sheet Data for Imposition ---
    const blankPageDataUrl = null; 
    const hasActualCover = pages.length > 0 && 
      (pages[0].layoutId === 'cover' || 
       (typeof pages[0].layoutId === 'string' && pages[0].layoutId.toLowerCase() === 'cover'));

    if (!hasActualCover) {
      // Add blank pages for the outside of the cover if no generated cover exists
      pdfPagesData.unshift(blankPageDataUrl); // Blank for outer-left of cover sheet
      pdfPagesData.push(blankPageDataUrl);    // Blank for outer-right of cover sheet
    }
    
    // Pad with blanks until total logical pages is a multiple of 4
    while (pdfPagesData.length % 4 !== 0) {
        pdfPagesData.push(blankPageDataUrl);
    }
    
    // If there *is* a cover, move its left half (back-cover) to the tail so the
    // standard imposition loop will place it on the outer-left of sheet #1.
    if (hasActualCover) {
      const backCoverHalf = pdfPagesData.splice(1, 1)[0];
      pdfPagesData.push(backCoverHalf);             // …now sits at the very end
    }
    
    // Build an array of sheet content objects, ordered from outermost physical sheet to innermost
    const sheetsInImpositionOrder = [];
    let currentLeftIdx = 0;
    let currentRightIdx = pdfPagesData.length - 1;
    const numPhysicalSheets = pdfPagesData.length / 4;

    for (let i = 0; i < numPhysicalSheets; i++) {
        sheetsInImpositionOrder.push({
            frontL_content: pdfPagesData[currentRightIdx],      // e.g., For outermost sheet: the last logical page (or blank)
            frontR_content: pdfPagesData[currentLeftIdx],       // e.g., For outermost sheet: the first logical page (or blank)
            backL_content: pdfPagesData[currentRightIdx - 1], // e.g., For outermost sheet: second-to-last logical page
            backR_content: pdfPagesData[currentLeftIdx + 1],   // e.g., For outermost sheet: second logical page
        });
        currentLeftIdx += 2;
        currentRightIdx -= 2;
    }

    // Reverse the order: innermost sheet will now be first, outermost (cover) sheet will be last.
    // This ensures the cover is the last page added to the PDFs.
    const finalSheetsForPdfGeneration = sheetsInImpositionOrder.reverse();


    // --- Part 3: Assemble into Two PDF Files (Fronts and Backs) ---
    const assemblyProgressMultiplier = 0.5;
    const startProgress = 100 * generationProgressMultiplier;
    const totalAssemblySheets = finalSheetsForPdfGeneration.length; 

    // --- PRINTER-SPECIFIC SETTING -----------------------------------------
    // For your printer (face-up re-feed, short-edge), backs do NOT need 180°
    const ROTATE_BACK_180 = false;

    let currentPdfFronts = null;
    let currentPdfBacks = null;
    let sheetsInCurrentChunk = 0;
    let chunkIndex = 1;

    for (let sheetIndex = 0; sheetIndex < totalAssemblySheets; sheetIndex++) {
        // Front sheet: innermost → outermost (unchanged)
        const sheetContent = finalSheetsForPdfGeneration[sheetIndex];
        // Back sheet: outermost → innermost (mirror order for face-up re-feed)
        const backSheetContent = finalSheetsForPdfGeneration[totalAssemblySheets - 1 - sheetIndex];
        const currentSheetNumberInPdfOrder = sheetIndex; // 0-indexed sheet in the PDF

        const currentAssemblyProgress = Math.round(((currentSheetNumberInPdfOrder + 1) / totalAssemblySheets) * 100 * assemblyProgressMultiplier);
        setPdfProgress(startProgress + currentAssemblyProgress);
        setPdfProgressLabel(`Generating ${pdfType} PDF... (Assembling Sheet ${currentSheetNumberInPdfOrder + 1}/${totalAssemblySheets}) Chunk ${chunkIndex}`);

        if (currentPdfFronts === null || sheetsInCurrentChunk >= MAX_SHEETS_PER_CHUNK) {
            if (currentPdfFronts !== null) {
                currentPdfFronts.save(`photobook_manual_fronts_part_${chunkIndex}.pdf`);
                currentPdfBacks.save(`photobook_manual_backs_part_${chunkIndex}.pdf`);
                chunkIndex++;
            }
            currentPdfFronts = new jsPDF('l', 'mm', 'a4');
            currentPdfBacks = new jsPDF('l', 'mm', 'a4');
            sheetsInCurrentChunk = 0;
        }

        if (sheetsInCurrentChunk > 0) { // Add new page to PDF if not the first sheet of the chunk
            currentPdfFronts.addPage();
            currentPdfBacks.addPage();
        }

        const frontL        = sheetContent.frontL_content;
        const frontR        = sheetContent.frontR_content;
        const backLContent  = backSheetContent.backL_content;
        const backRContent  = backSheetContent.backR_content;

        // Add front sides to the "Fronts" PDF
        if (frontL) currentPdfFronts.addImage(frontL, 'PNG', 0, 0, pdfWidth / 2, pdfHeight);
        if (frontR) currentPdfFronts.addImage(frontR, 'PNG', pdfWidth / 2, 0, pdfWidth / 2, pdfHeight);

        // Rotate and add back sides to the "Backs" PDF
        const backLRotated = backLContent && ROTATE_BACK_180
            ? await rotateImage(backLContent, 180)
            : backLContent;
        const backRRotated = backRContent && ROTATE_BACK_180
            ? await rotateImage(backRContent, 180)
            : backRContent;
        /*  Your printer feeds the sheet top-edge-first and prints backs  *
         *  without rotation, so we must mirror the logical halves:       *
         *  logical back-RIGHT → physical LEFT side of sheet              *
         *  logical back-LEFT  → physical RIGHT side of sheet             */
        if (backRRotated) currentPdfBacks.addImage(backRRotated, 'PNG',
                                                  0, 0, pdfWidth / 2, pdfHeight);        // ← left half
        if (backLRotated) currentPdfBacks.addImage(backLRotated, 'PNG',
                                                  pdfWidth / 2, 0, pdfWidth / 2, pdfHeight); // → right half

        sheetsInCurrentChunk++;
        await new Promise(resolve => setTimeout(resolve, 0)); // Yield for UI updates
    }

    // Save the last chunk if it exists
    if (currentPdfFronts !== null && sheetsInCurrentChunk > 0) {
        currentPdfFronts.save(`photobook_manual_fronts_part_${chunkIndex}.pdf`);
        currentPdfBacks.save(`photobook_manual_backs_part_${chunkIndex}.pdf`);
    }
  };
  
  // Removed border width scaling - now handled directly by PhotoBookLayout

  const handlePlaceholderUpdate = (layoutGlobalIndex, placeholderIdxOnSpread, newPartialOverrides) => {
    // Call the new prop passed from App.js
    if (onLayoutPlanUpdate) {
      onLayoutPlanUpdate(layoutGlobalIndex, placeholderIdxOnSpread, newPartialOverrides);
    } else {
      console.warn("PhotoBookRenderer: onLayoutPlanUpdate prop is not defined.");
    }
  };

  return (
    <div>
      {/* Apply fixed width only when generating PDF to ensure correct scaling */}
      <div style={{ width: isPrintLayout ? '3508px' : '90%', margin: '0 auto' }}>
        {(() => { // IIFE to manage local state for this map run
          const localRenderedBatchIds = new Set();
          return pages.map((page, index) => {

            // Get layout for rendering and batch checking
            getLayoutById(page.layoutId); // We still need to get the layout for rendering purposes
            const isFirstInBatch = page.ai_batch_id && !localRenderedBatchIds.has(page.ai_batch_id);
            if (isFirstInBatch) localRenderedBatchIds.add(page.ai_batch_id);

            return (
              <div key={index} style={{ position: 'relative', margin: '20px 0' }} ref={el => layoutRefs.current[index] = el}>
                {!isPrintLayout && (
                  <div
                    style={{
                      position: 'absolute',
                      top: 10,
                      right: 10,
                      zIndex: 20,
                      display: 'flex',
                      gap: '8px',
                      backgroundColor: 'rgba(255,255,255,0.8)',
                      padding: '4px 6px',
                      borderRadius: '4px'
                    }}
                  >
                    {isFirstInBatch && onRequestEditBatch && (
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => onRequestEditBatch(page.ai_batch_id)}
                      >
                        ✏️ Edit Set
                      </Button>
                    )}
                    <Button
                      variant={editingSpreadIndex === index ? 'contained' : 'outlined'}
                      size="small"
                      onClick={() =>
                        setEditingSpreadIndex(
                          editingSpreadIndex === index ? null : index
                        )
                      }
                    >
                      🔧 Edit placeholders
                    </Button>
                  </div>
                  )}
                <PhotoBookLayout
                  layout={getLayoutById(page.layoutId)}
                  images={typeof page.photos[0] === 'string' 
                    ? page.photos 
                    : page.photos.map(photo => photo.url || photo.baseUrl)}
                  photoObjects={page.photos}
                  // Pass new per-page props directly from `page`
                  leftPageText={page.leftPageText}
                  rightPageText={page.rightPageText}
                  leftPageDisplayDate={page.leftPageDisplayDate}
                  leftPageDisplayCity={page.leftPageDisplayCity}
                  rightPageDisplayDate={page.rightPageDisplayDate}
                  rightPageDisplayCity={page.rightPageDisplayCity}
                  adjustedLeftPageFontSize={page.adjustedLeftPageFontSize}
                  adjustedRightPageFontSize={page.adjustedRightPageFontSize}
                  
                  // Other props
                  photo_place_names={page.photo_place_names}
                  leftPageNumber={index * 2 + 1}
                  rightPageNumber={index * 2 + 2}
                  totalPages={pages.length * 2}
                  isPrintLayout={isPrintLayout}
                  onPhotoCropChange={(photoIndex, cropInfo) => handlePhotoCropChange(index, photoIndex, cropInfo)}
                  leftPageData={page.leftPageData}
                  rightPageData={page.rightPageData}
                  // Props for placeholder editing
                  isPlaceholderEditingActive={editingSpreadIndex === index}
                  placeholderEditData={
                    // Convert page.placeholderModifications (array) to an object for PhotoBookLayout
                    (page.placeholderModifications || []).reduce((acc, mod, phIdx) => {
                      if (mod) { // Only include if there's an actual modification object
                        acc[phIdx] = mod;
                      }
                      return acc;
                    }, {})
                  }
                  onPlaceholderUpdate={(placeholderIdxOnSpread, newOverrides) => handlePlaceholderUpdate(index, placeholderIdxOnSpread, newOverrides)}
                  layoutIndex={index} // Pass layoutIndex to PhotoBookLayout
                />
              </div>
            );
          });
        })()}
      </div>

      <div>
        <label>
          <input
            type="radio"
            value="normal"
            checked={pdfType === 'normal'}
            onChange={() => setPdfType('normal')}
          />
          Normal PDF
        </label>
        <label>
          <input
            type="radio"
            value="booklet"
            checked={pdfType === 'booklet'}
            onChange={() => setPdfType('booklet')}
          />
          Booklet PDF (for double-sided printing)
        </label>
        <label>
          <input
            type="radio"
            value="manual-booklet"
            checked={pdfType === 'manual-booklet'}
            onChange={() => setPdfType('manual-booklet')}
          />
          Manual Booklet (2-Step Print)
        </label>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', margin: '20px 0' }}>
        <div>
          <button onClick={generatePDF} style={{ marginRight: '10px' }}>
            Generate PDF
          </button>
          <button onClick={saveAlbum} style={{ marginRight: '10px' }}>
            Save Album
          </button>
          <label style={{ 
            display: 'inline-block', 
            padding: '8px 12px', 
            backgroundColor: '#1976d2', 
            color: 'white', 
            borderRadius: '4px',
            cursor: 'pointer'
          }}>
            Load Album
            <input 
              type="file" 
              accept=".json" 
              onChange={loadAlbumFromFile} 
              style={{ display: 'none' }} 
            />
          </label>
        </div>
      </div>
    </div>
  );
};

const getLayoutById = (id) => {
  const layout = layouts.find(layout => layout.id === id);
  if (!layout) {
    console.warn(`Layout with id ${id} not found. Using first layout as fallback.`);
    return layouts[0];  // Fallback to the first layout if not found
  }
  return layout;
};

export function create_photo_book() {
  return new PhotoBook();
}

export default PhotoBook;
